import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';

import 'rxjs/add/operator/map';

import { TableConfig } from "@app/shared/util/table-config.utils";
import { AppUtils } from "@app/shared/util/app.utils";

import { ToolRequest } from "@models/toolRequests.model";
import { PagedResponse } from '@models/response.model';

import { GroupChildWrapper } from "@app/tool/model/group-child-wrapper.model";
import { Group } from "@app/tool/model/group.model";
import { Tool } from "@app/tool/model/tool.model";

import { environment } from 'environments/environment';
import { Section } from "@models/enums/section.utils";
import { ToolReturnToolsFilterModel } from "@models/tool-return-tools-filter.model";
import { map } from "rxjs/operators";

/**
 * Service class for handling CRUD operations on {@link ToolRequest} objects.
 */
@Injectable()
export class ToolRequestsService {
    static readonly API_TOOL_REQUESTS_BASE_PATH = `${environment.baseURL}/api/toolRequests`;
    static readonly API_TOOL_RETURNS_BASE_PATH = `${environment.baseURL}/api/toolReturn`;

    constructor(private http: HttpClient) {
    }

    getAllToolRequestsForLine(lineId: number) {
        let params = new HttpParams().set('lineId', `${lineId}`);
        params = params.set('IsAvailbleForReturn', `true`);

        return this.http.get<PagedResponse<ToolRequest>>(`${ToolRequestsService.API_TOOL_REQUESTS_BASE_PATH}`,
            {params: params}
        ).pipe(map(this.mapToolRequests));
    }

    getAllToolRequests(filter: TableConfig, type = 'LINE', section: Section) {
        let params = AppUtils.buildPageableParamsFromList(filter);
        params = params.set('type', type);
        params = params.set('section', section ? section.toString() : null);

        return this.http.get<PagedResponse<ToolRequest[]>>(`${ToolRequestsService.API_TOOL_REQUESTS_BASE_PATH}`,
            {params})
            .pipe(map(this.mapToolRequests));
    }

    getToolRequestDetails(id: number) {
        return this.http.get<PagedResponse<ToolRequest>>(`${ToolRequestsService.API_TOOL_REQUESTS_BASE_PATH}/${id}`)
            .pipe(map(this.mapToolRequest));
    }

    getToolRequestTools(id: number, filter = new TableConfig()) {
        const params = AppUtils.buildPageableParams(filter);

        return this.http.get<PagedResponse<Tool>>(`${ToolRequestsService.API_TOOL_REQUESTS_BASE_PATH}/${id}/tools`,
            {params: params})
            .pipe(map(this.mapGroups));
    }

    getToolReturnRequestsByToolRequest(toolRequest: ToolRequest, filter: TableConfig, filterParams: ToolReturnToolsFilterModel) {
        let params = AppUtils.buildPageableParams(filter);
        params = params.set('LineId', toolRequest.line.id + '');
        params = params.set('ToolRequestId', toolRequest.id + '');

        if (filterParams && filterParams.hasMaintenanceAtLine !== undefined) {
            params = params.set('HasMaintenanceAtLine', filterParams.hasMaintenanceAtLine + '');
        }
        if (filterParams && filterParams.excludedFromMaintenance !== undefined) {
            params = params.set('excludedFromMaintenance', filterParams.excludedFromMaintenance + '');
        }

        return this.http.get<PagedResponse<ToolRequest>>(`${ToolRequestsService.API_TOOL_RETURNS_BASE_PATH}/tools`,
            {params: params})
            .pipe(map(this.mapGroups));
    }

    replaceTool(toolRequest: ToolRequest, oldToolId: number, newToolId: number) {
        return this.http.patch<number>(`${ToolRequestsService.API_TOOL_REQUESTS_BASE_PATH}/${toolRequest.id}/replaceTool`, {
            oldToolId: oldToolId,
            newToolId: newToolId
        })
    }

    repairTool(toolRequest: ToolRequest, toolId: number) {
        return this.http.patch<number>(`${ToolRequestsService.API_TOOL_REQUESTS_BASE_PATH}/${toolRequest.id}/repairTool`, {
            toolId: toolId
        });
    }

    createToolRequest(lineId: number,
                      partNumberId: number,
                      priority: boolean,
                      priorityReason: string,
                      toolsIDS: number[],
                      hasToolsThatNeedToBeMoved: boolean,
                      insertionCycleId: number = null) {
        const params = {
            lineId: lineId,
            partNumberId: partNumberId,
            highPriority: priority,
            priorityReason: priorityReason,
            toolsId: toolsIDS,
            insertionCycleId: insertionCycleId,
            hasToolsThatNeedToBeMoved: hasToolsThatNeedToBeMoved
        };
        if (!params.highPriority) {
            delete params['priorityReason'];
        }
        if (!partNumberId) {
            delete params['partNumber'];
        }
        if (!insertionCycleId) {
            delete params['insertionCycleId']
        }
        return this.http.post<ToolRequest>(`${ToolRequestsService.API_TOOL_REQUESTS_BASE_PATH}`, params);
    }

    getReplacementToolsForToolId(id: number, toolId: number) {
        const params = new HttpParams().set('toolId', `${toolId}`);
        return this.http.get<Tool[]>(`${ToolRequestsService.API_TOOL_REQUESTS_BASE_PATH}/${id}/replacementTools`,
            {params: params})
            .pipe(map(this.mapTools));
    }

    approveToolRequestById(id: number) {
        return this.http.patch<Object>(`${ToolRequestsService.API_TOOL_REQUESTS_BASE_PATH}/${id}/approve`, {});
    }

    rejectToolRequestById(id: number, data) {
        return this.http.patch(`${ToolRequestsService.API_TOOL_REQUESTS_BASE_PATH}/${id}/reject`, data);
    }

    startApprovalById(id: number) {
        return this.http.patch<Object>(`${ToolRequestsService.API_TOOL_REQUESTS_BASE_PATH}/${id}/startApprove`, {});
    }

    startProcessingById(id: number) {
        return this.http.patch<Object>(`${ToolRequestsService.API_TOOL_REQUESTS_BASE_PATH}/${id}/startProcessing`, {});
    }

    processById(id: number, boschId: string) {
        let headers = new HttpHeaders();
        if (boschId) {
            headers = headers.set("BoschId", boschId);
        }

        return this.http.patch<Object>(`${ToolRequestsService.API_TOOL_REQUESTS_BASE_PATH}/${id}/process`, {}, {headers: headers});
    }

    cancelToolReturnRequest(toolRequest: ToolRequest) {
        return this.http.patch<ToolRequest>(`${ToolRequestsService.API_TOOL_REQUESTS_BASE_PATH}/${toolRequest.id}/cancel`,
            {closeReason: toolRequest.closeReason});
    }

    private mapToolRequests = res => {
        return {
            items: res.items.map(toolRequest => new ToolRequest(toolRequest)),
            total: res.total
        };
    };

    private mapGroups = res => {
        const body = res;
        return {
            items: this.mapGroupableItems(body.items),
            total: body.total
        };
    };

    private mapToolRequest = res => {
        return new ToolRequest(res);
    };

    private mapTools = res => {
        return res.map(tool => new Tool(tool));
    };

    private mapGroupableItems(items) {
        const groups = items.map(group => {
            if (group.subGroup) {
                return new Group(group);
            }
            return new GroupChildWrapper(group).item;
        });
        return groups;
    }

}
